const {
  formatMoney,
  getElements,
  getElement,
  translateString,
} = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

/**
 * @typedef {Object} AdditionalVariantData
 *
 * @property {Record<string, number>} stockData
 */

/**
 * @typedef {CustomEvent<{ quantity: number }>} ProductPriceUpdateEvent
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  backInStock: '[data-product-back-in-stock]',
  controlsAtc: '[data-product-controls-atc]',
  controlsBis: '[data-product-controls-bis]',
  bespokeAtc: '[data-bespoke-atc]',
  controlsHiddenWhenUnavailable:
    '[data-product-controls-hidden-when-unavailable]',
  form: '[data-product-form]',
  giftDrawerTrigger: 'gift-bar',
  lowStockMessage: '[data-low-stock-message]',
  price: '[data-product-price]',
  priceCompare: '[data-product-price-compare]',
  priceCompareText: '[data-product-price-compare-text]',
  signupFormVariantId: '[data-product-signup-form-variant-id]',
  sku: '[data-product-sku]',
  variants: '[data-product-variants]',
  mostPopular: '[data-most-popular]',
  inStockText: '[data-product-delivery-message]',
}

/**
 * @note if there are no variants, the Variant class doesn't
 * get instantiated, hence we add a global listener
 */
document.body.addEventListener('product-price:update', onPriceUpdate)

/** @param {ProductPriceUpdateEvent} event */
function onPriceUpdate (event) {
  const variants = JSON.parse(
    document.querySelector(selectors.variants).textContent,
  )
  const variantId = new URL(window.location).searchParams.get('variant')
  const currentVariant = variantId
    ? variants.find(variant => variant.id === Number(variantId))
    : variants[0]

  setPrice(currentVariant, event.detail.quantity)
}

class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }

  /** @param {number} quantity */
  setPrice (quantity = 1) {
    setPrice(this.data, quantity)
  }

  setAvailability () {
    getElements(selectors.addToCart).forEach(
      el => (el.disabled = !this.data.available),
    )

    getElements(selectors.addToCartText).forEach(
      el =>
        (el.textContent = this.data.available
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut),
    )

    getElements(selectors.backInStock).forEach(el =>
      el.setAttribute('data-bis-variant-id', this.data.id),
    )

    getElements(selectors.controlsAtc).forEach(el =>
      el.setAttribute('aria-hidden', !this.data.available),
    )
    getElements(selectors.controlsBis).forEach(el =>
      el.setAttribute('aria-hidden', this.data.available),
    )
    getElements(selectors.bespokeAtc).forEach(el =>
      el.setAttribute('aria-hidden', !this.data.available),
    )

    getElements(selectors.controlsHiddenWhenUnavailable).forEach(el =>
      el.setAttribute('aria-hidden', !this.data.available),
    )
  }

  updateGiftDrawerTrigger () {
    const triggers = getElements(selectors.giftDrawerTrigger)

    triggers.forEach(
      trigger => (trigger.dataset.variantId = this.id.toString()),
    )
  }

  updateSignupForm () {
    getElements(selectors.signupFormVariantId).forEach(input => {
      input.value = this.id
    })
  }

  updateInStockText (available = false) {
    const inStockText = getElement(selectors.inStockText)
    const bis = getElement(selectors.controlsBis)

    if (!inStockText || !bis) {
      return
    }

    if (available) {
      inStockText.setAttribute('aria-hidden', 'false')
    } else {
      inStockText.setAttribute('aria-hidden', 'true')
    }
  }

  updateInput () {
    const inputs = getElements(
      `form[action*="/cart/add"]:not([data-ignore-variants]) [name="items[0][id]"], form[action*="/cart/add"]:not([data-ignore-variants]) [name="id"]`,
    )

    inputs.forEach(input => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  setLowStockMessage () {
    const lowStockMessages = getElements(selectors.lowStockMessage)
    const { stock } = this.data.additionalData

    lowStockMessages.forEach(message => {
      const { lowStockMessageTemplate, lowStockThreshold } = message.dataset

      const hasLowStock = stock < lowStockThreshold && stock > 0

      message.setAttribute('aria-hidden', !hasLowStock)
      message.innerText = translateString(lowStockMessageTemplate, {
        count: stock,
      })
    })
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach(shareButton => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    const url = new URL(window.location.href)
    url.searchParams.set('variant', this.id.toString())

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant)]

    this.quantity = 1
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)

    /**
     * @note if there are variants, let this class take
     * responsibility for rendering the new price
     */
    document.body.removeEventListener('product-price:update', onPriceUpdate)
    this.listeners.add(
      document.body,
      'product-price:update',
      this.onPriceUpdate.bind(this),
    )
  }

  /** @param {ProductPriceUpdateEvent} event */
  onPriceUpdate (event) {
    this.quantity = event.detail.quantity

    const variant = this.getVariant()

    variant.setPrice(this.quantity)
  }

  onVariantChange () {
    const variant = this.getVariant()

    variant.setAvailability()
    variant.setPrice(this.quantity)
    variant.updateGiftDrawerTrigger()
    variant.updateInput()
    variant.setLowStockMessage()
    variant.updateSignupForm()
    variant.updateInStockText(this.currentVariant.available)

    if (!this.currentVariant) {
      this.resetPage(variant)

      return
    }

    if (this.shouldUpdateUrl) {
      variant.setUrl()
    }
    variant.setShareUrl()

    this.updateDrawerToggles()

    if (this.closeDrawersOnChange) {
      const drawersController = document.querySelector('site-drawers')

      drawersController.activeDrawerKey = ''
    }
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      variant => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  updateDrawerToggles () {
    const drawerToggleLabels =
      this.externalEls['drawer-toggle-label']?.elements || []
    const drawerToggleIcons =
      this.externalEls['drawer-toggle-icon']?.elements || []

    drawerToggleLabels.forEach(label => {
      const option = this.selectedOptions[label.dataset.optionPosition - 1]
      const content = translateString(this.dataset.drawerLabelTemplate, {
        variant: option.label,
        value: option.value,
      })

      const mostPopular = label.dataset?.optionMostPopular

      if (mostPopular && mostPopular.toString() === content.toLowerCase()) {
        getElement(selectors.mostPopular).setAttribute('aria-hidden', 'false')
      } else {
        getElement(selectors.mostPopular).setAttribute('aria-hidden', 'true')
      }

      label.textContent = content
    })

    drawerToggleIcons.forEach(icon => {
      const option = this.selectedOptions[icon.dataset.optionPosition - 1]
      icon.textContent = option.icon ?? option.value[0].toUpperCase()
    })
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find(variant => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {boolean}
   */
  get shouldUpdateUrl () {
    return this.getAttribute('data-should-update-url') !== 'false'
  }

  /**
   * @returns {(ShopifyVariant & AdditionalVariantData)[]}
   */
  get data () {
    const variants = JSON.parse(this.els.data.element.innerText)
    const additionalData = JSON.parse(
      this.els['additional-data'].element.innerText,
    )

    variants.forEach(variant => {
      variant.additionalData = {
        stock: additionalData.stockData[variant.id],
      }
    })

    return variants
  }

  get closeDrawersOnChange () {
    return this.dataset.closeDrawersOnChange === 'true'
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find(el => el.checked).value
      const icon = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find(el => el.checked).dataset.icon

      return {
        label: name,
        value: choice,
        icon,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}

function hideElements (elements) {
  elements.forEach(el => (el.hidden = true))
}

function unhideElements (elements) {
  elements.forEach(el => (el.hidden = false))
}

function unhidePriceEls () {
  unhideElements(getElements(`${selectors.price}, ${selectors.priceCompare}`))
}

function hidePriceCompareElements () {
  hideElements(getElements(selectors.priceCompare))
}

function getPriceAddon () {
  return Number.parseInt(document.body.dataset.postPriceAddon || '0', 10) || 0
}

function setPrice (data, quantity = 1) {
  unhidePriceEls()

  const addon = getPriceAddon()
  const price = (data.price + addon) * quantity
  const compareAtPrice = data.compare_at_price
    ? (data.compare_at_price + addon) * quantity
    : 0

  const priceElements = getElements(selectors.price)
  priceElements.forEach(el => {
    if (el.dataset.dontUpdate === 'true') {
      return
    }

    el.innerHTML = formatMoney(price)
    /** @todo stop hardcoding classname */
    el.classList.toggle('prd-DetailPrice_Price-sale', compareAtPrice)
  })

  const priceCompareElements = getElements(selectors.priceCompare)
  priceCompareElements.forEach(
    el => (el.innerHTML = formatMoney(compareAtPrice)),
  )

  if (!compareAtPrice) {
    hidePriceCompareElements()
  }
}
